import { useState, useMemo } from 'react';
import { demoRooms, demoGuests, demoBookings, demoRoomBeds } from '@/mocks/demoData';
import type { Guest, Booking, Payment } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';
import { addPayment, generateReceiptNo, generatePaymentId, getTotalPaidForBooking } from '@/utils/paymentStore';

const ID_TYPES = ['Aadhaar', 'Passport', 'Driving License'];
const BOOKING_TYPES = ['walk_in', 'advance', 'pre_booking'];

export default function ReceptionCheckin() {
  const { addToast } = useApp();
  const [step, setStep] = useState<'search' | 'form' | 'confirm'>('search');
  const [searchPhone, setSearchPhone] = useState('');
  const [matchedGuest, setMatchedGuest] = useState<Guest | null>(null);
  const [bookings, setBookings] = useState<Booking[]>(demoBookings);
  const [guests, setGuests] = useState<Guest[]>(demoGuests);
  const [rooms, setRooms] = useState(demoRooms);
  const [confirmBooking, setConfirmBooking] = useState<Booking | null>(null);
  const [balancePaymentAmount, setBalancePaymentAmount] = useState<number>(0);
  const [guestWillPayLater, setGuestWillPayLater] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<Payment['payment_method']>('cash');
  const [arrivalPaymentMethod, setArrivalPaymentMethod] = useState<Payment['payment_method']>('cash');
  const [guestForm, setGuestForm] = useState({
    full_name: '',
    phone: '',
    email: '',
    id_proof_type: 'Aadhaar',
    id_proof_number: '',
    address: '',
  });
  const [bookingForm, setBookingForm] = useState({
    room_id: '',
    check_in: '',
    check_out: '',
    booking_type: 'walk_in',
    deposit: '0',
  });
  const [selectedBeds, setSelectedBeds] = useState<string[]>([]);
  const [bookingMode, setBookingMode] = useState<'full_room' | 'bed'>('full_room');

  const availableRooms = rooms.filter((r) => r.status === 'available');
  const sharedDormRooms = rooms.filter((r) => (r.room_type === 'shared' || r.room_type === 'dormitory'));
  const getBedsForRoom = (roomId: string) => demoRoomBeds.filter((b) => b.room_id === roomId);

  const todayArrivals = useMemo(() => {
    const today = '2026-05-15';
    return bookings.filter((b) =>
      new Date(b.check_in).toISOString().slice(0, 10) === today &&
      b.status === 'confirmed'
    );
  }, [bookings]);

  // Handle walk-in search: go to full form
  const handleSearch = () => {
    const guest = guests.find((g) =>
      g.phone.includes(searchPhone) ||
      g.full_name.toLowerCase().includes(searchPhone.toLowerCase())
    );
    if (guest) {
      setMatchedGuest(guest);
      setGuestForm({
        full_name: guest.full_name,
        phone: guest.phone,
        email: guest.email,
        id_proof_type: guest.id_proof_type,
        id_proof_number: guest.id_proof_number,
        address: guest.address,
      });
      addToast('success', 'Guest found! Details loaded.');
    } else {
      setMatchedGuest(null);
      setGuestForm({ ...guestForm, phone: searchPhone });
      addToast('info', 'No existing guest found. Please fill in details.');
    }
    setStep('form');
  };

  // One-click check-in from Expected Arrivals (advance booking)
  const handleArrivalCheckin = (booking: Booking) => {
    setConfirmBooking(booking);
    setBalancePaymentAmount(0);
    setGuestWillPayLater(false);
    setStep('confirm');
  };

  // Confirm the advance booking — update existing booking status
  const confirmArrival = () => {
    if (!confirmBooking) return;

    const effectivePending = Math.max(0, confirmBooking.pending_amount - balancePaymentAmount);

    // Create payment record if amount collected at check-in
    if (balancePaymentAmount > 0) {
      const receiptNo = generateReceiptNo();
      const totalPaidSoFar = getTotalPaidForBooking(confirmBooking.id) + confirmBooking.deposit;
      const payment: Payment = {
        id: generatePaymentId(),
        booking_id: confirmBooking.id,
        amount: balancePaymentAmount,
        payment_method: arrivalPaymentMethod,
        payment_type: (totalPaidSoFar + balancePaymentAmount) >= confirmBooking.total_amount ? 'full' : 'partial',
        receipt_no: receiptNo,
        created_by: 'usr-003',
        created_at: new Date().toISOString(),
      };
      addPayment(payment);
    }

    setBookings((prev) =>
      prev.map((b) =>
        b.id === confirmBooking.id
          ? {
              ...b,
              status: 'checked_in',
              deposit: b.deposit + balancePaymentAmount,
              pending_amount: effectivePending,
            }
          : b
      )
    );

    if (!confirmBooking.is_bed_booking) {
      setRooms((prev) =>
        prev.map((r) =>
          r.id === confirmBooking.room_id ? { ...r, status: 'occupied' as const } : r
        )
      );
    }

    const guest = guests.find((g) => g.id === confirmBooking.guest_id);
    const paymentMsg = balancePaymentAmount > 0
      ? ` Rs. ${balancePaymentAmount.toLocaleString('en-IN')} collected.`
      : '';
    const pendingMsg = effectivePending > 0
      ? ` Rs. ${effectivePending.toLocaleString('en-IN')} pending — guest will pay later.`
      : '';
    addToast('success', `${guest?.full_name || 'Guest'} checked in!${paymentMsg}${pendingMsg}`);

    // Reset
    setStep('search');
    setConfirmBooking(null);
    setBalancePaymentAmount(0);
    setGuestWillPayLater(false);
    setSearchPhone('');
  };

  // Walk-in / new guest check-in form submit
  const handleWalkinCheckin = () => {
    if (!guestForm.full_name || !guestForm.phone || !bookingForm.room_id || !bookingForm.check_in || !bookingForm.check_out) {
      addToast('error', 'Please fill all required fields');
      return;
    }
    if (bookingMode === 'bed' && selectedBeds.length === 0) {
      addToast('error', 'Select at least one bed');
      return;
    }
    const room = rooms.find((r) => r.id === bookingForm.room_id);
    if (!room) return;

    const nights = Math.max(1, Math.ceil((new Date(bookingForm.check_out).getTime() - new Date(bookingForm.check_in).getTime()) / (1000 * 60 * 60 * 24)));
    const totalAmount = bookingMode === 'bed'
      ? (room.per_bed_price || 0) * selectedBeds.length * nights
      : room.tariff * nights;

    let guestId = matchedGuest?.id;
    if (!guestId) {
      const newGuest: Guest = {
        id: `guest-${Date.now()}`,
        ...guestForm,
        is_repeating: false,
        total_spend: 0,
        created_at: new Date().toISOString(),
      };
      setGuests((prev) => [...prev, newGuest]);
      guestId = newGuest.id;
    }

    const newBooking: Booking = {
      id: `bk-${Date.now()}`,
      booking_id: `HOTEL-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${bookings.length + 1}`,
      guest_id: guestId!,
      room_id: bookingForm.room_id,
      booking_type: bookingForm.booking_type,
      is_bed_booking: bookingMode === 'bed',
      bed_ids: bookingMode === 'bed' ? selectedBeds : [],
      bed_count: bookingMode === 'bed' ? selectedBeds.length : 0,
      check_in: new Date(bookingForm.check_in).toISOString(),
      check_out: new Date(bookingForm.check_out).toISOString(),
      status: 'checked_in',
      deposit: Number(bookingForm.deposit),
      total_amount: totalAmount,
      pending_amount: totalAmount - Number(bookingForm.deposit),
      extra_charges: [],
      cancellation_reason: '',
      refund_status: '',
      created_at: new Date().toISOString(),
    };

    // Create payment record if deposit > 0
    const depositAmount = Number(bookingForm.deposit);
    if (depositAmount > 0) {
      const receiptNo = generateReceiptNo();
      const payment: Payment = {
        id: generatePaymentId(),
        booking_id: newBooking.id,
        amount: depositAmount,
        payment_method: paymentMethod,
        payment_type: depositAmount >= totalAmount ? 'full' : 'deposit',
        receipt_no: receiptNo,
        created_by: 'usr-003',
        created_at: new Date().toISOString(),
      };
      addPayment(payment);
      addToast('success', `Receipt ${receiptNo} — ${paymentMethod.toUpperCase()}`);
    }

    setBookings((prev) => [...prev, newBooking]);
    if (bookingMode === 'full_room') {
      setRooms((prev) => prev.map((r) => r.id === bookingForm.room_id ? { ...r, status: 'occupied' as const } : r));
    }

    const modeLabel = bookingMode === 'bed' ? `${selectedBeds.length} bed(s)` : 'full room';
    addToast('success', `Walk-in check-in done! Booking ID: ${newBooking.booking_id} (${modeLabel})`);

    // Reset
    setStep('search');
    setSearchPhone('');
    setMatchedGuest(null);
    setGuestForm({ full_name: '', phone: '', email: '', id_proof_type: 'Aadhaar', id_proof_number: '', address: '' });
    setBookingForm({ room_id: '', check_in: '', check_out: '', booking_type: 'walk_in', deposit: '0' });
    setBookingMode('full_room');
    setSelectedBeds([]);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">Guest Check-in</h1>
        <p className="text-navy-500 mt-1">One-click check-in for advance bookings, full form for walk-in guests.</p>
      </div>

      {/* ===== STEP: SEARCH (main screen) ===== */}
      {step === 'search' && (
        <div className="space-y-6">
          {/* Quick Check-in: Expected Arrivals Today */}
          {todayArrivals.length > 0 && (
            <div className="bg-white rounded-xl border border-green-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-green-600 text-lg" />
                </div>
                <div>
                  <h3 className="font-semibold text-navy-800">Expected Arrivals Today</h3>
                  <p className="text-xs text-navy-500">Pre-booked guests — one click to check in</p>
                </div>
                <span className="ml-auto text-sm font-semibold text-green-700 bg-green-50 px-3 py-1 rounded-full">
                  {todayArrivals.length} guest{todayArrivals.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {todayArrivals.map((b) => {
                  const g = guests.find((guest) => guest.id === b.guest_id);
                  const r = rooms.find((room) => room.id === b.room_id);
                  return (
                    <button
                      key={b.id}
                      onClick={() => handleArrivalCheckin(b)}
                      className="text-left p-4 rounded-lg border border-green-100 bg-green-50/50 hover:bg-green-50 hover:border-green-300 transition-all cursor-pointer group"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-green-700" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-navy-800 text-sm truncate">{g?.full_name || 'Guest'}</div>
                          <div className="text-xs text-navy-500">{g?.phone || ''}</div>
                        </div>
                        <i className="ri-arrow-right-line text-green-400 group-hover:text-green-600 transition-colors" />
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className="px-2 py-1 bg-white rounded-md text-navy-600 border border-navy-100">
                          <i className="ri-hotel-line mr-1" />Room {r?.room_number} — {r?.category}
                        </span>
                        <span className="px-2 py-1 bg-white rounded-md text-navy-600 border border-navy-100">
                          <i className="ri-calendar-line mr-1" />
                          {new Date(b.check_in).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} → {new Date(b.check_out).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </span>
                        <span className="px-2 py-1 bg-white rounded-md text-navy-600 border border-navy-100">
                          <i className="ri-bank-card-line mr-1" />Rs. {b.total_amount.toLocaleString('en-IN')}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                          {b.booking_type.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                        <span className="text-xs text-navy-400">{b.booking_id}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Walk-in Search */}
          <div className="bg-white rounded-xl border border-navy-100 p-8">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-16 h-16 rounded-2xl bg-navy-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-search-line text-navy-600 text-2xl" />
              </div>
              <h3 className="text-lg font-semibold text-navy-800 mb-2">Walk-in Guest Check-in</h3>
              <p className="text-sm text-navy-500 mb-6">For guests without advance booking — search existing profile or create new.</p>
              <div className="flex gap-3">
                <input
                  type="text"
                  className="flex-1 px-4 py-3 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  placeholder="Phone number or guest name..."
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                  onClick={handleSearch}
                  disabled={!searchPhone.trim()}
                  className="px-6 py-3 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-search-line mr-2" />
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== STEP: CONFIRM (one-click advance booking check-in) ===== */}
      {step === 'confirm' && confirmBooking && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl border border-green-200 overflow-hidden">
            {/* Header */}
            <div className="bg-green-50 px-6 py-5 border-b border-green-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-green-200 flex items-center justify-center">
                  <i className="ri-check-double-line text-green-700 text-xl" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-navy-800">Confirm Check-in</h3>
                  <p className="text-sm text-navy-500">Pre-booked guest — all details already filled from booking</p>
                </div>
              </div>
            </div>

            {/* Booking Summary */}
            <div className="p-6 space-y-5">
              {(() => {
                const g = guests.find((guest) => guest.id === confirmBooking.guest_id);
                const r = rooms.find((room) => room.id === confirmBooking.room_id);
                const checkInDate = new Date(confirmBooking.check_in);
                const checkOutDate = new Date(confirmBooking.check_out);
                const nights = Math.max(1, Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24)));

                return (
                  <>
                    {/* Guest Info */}
                    <div className="p-4 bg-navy-50 rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-navy-200 flex items-center justify-center">
                          <i className="ri-user-3-line text-navy-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-navy-800">{g?.full_name || 'Guest'}</div>
                          <div className="text-xs text-navy-500">{g?.phone}</div>
                        </div>
                        {g?.is_repeating && (
                          <span className="ml-auto text-xs px-2 py-1 bg-gold-100 text-gold-700 rounded-full font-medium">
                            <i className="ri-star-line mr-1" />Repeat
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                        <div>
                          <span className="text-navy-400">Email:</span>
                          <span className="ml-2 text-navy-700">{g?.email || '—'}</span>
                        </div>
                        <div>
                          <span className="text-navy-400">ID:</span>
                          <span className="ml-2 text-navy-700">{g?.id_proof_type}: {g?.id_proof_number}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-navy-400">Address:</span>
                          <span className="ml-2 text-navy-700">{g?.address || '—'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Room & Booking Info */}
                    <div className="p-4 bg-navy-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-navy-700 mb-3">Room & Booking Details</h4>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-navy-500">Room</span>
                          <span className="font-medium text-navy-800">{r?.room_number} — {r?.category}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-navy-500">Booking ID</span>
                          <span className="font-medium text-navy-800">{confirmBooking.booking_id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-navy-500">Type</span>
                          <span className="font-medium text-navy-800">
                            {confirmBooking.is_bed_booking ? `${confirmBooking.bed_count} Bed(s)` : 'Full Room'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-navy-500">Booking</span>
                          <span className="font-medium text-navy-800 capitalize">{confirmBooking.booking_type.replace('_', ' ')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-navy-500">Check-in</span>
                          <span className="font-medium text-navy-800">
                            {checkInDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-navy-500">Check-out</span>
                          <span className="font-medium text-navy-800">
                            {checkOutDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-navy-500">Nights</span>
                          <span className="font-medium text-navy-800">{nights}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-navy-500">Status</span>
                          <span className="font-medium text-blue-600">Confirmed → Checking in now</span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Summary */}
                    <div className="p-4 bg-navy-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-navy-700 mb-3">Payment Summary</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-navy-500">Total Amount</span>
                          <span className="font-semibold text-navy-800">Rs. {confirmBooking.total_amount.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-navy-500">Deposit Paid (Advance)</span>
                          <span className="text-green-700 font-medium">Rs. {confirmBooking.deposit.toLocaleString('en-IN')}</span>
                        </div>
                        {balancePaymentAmount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-navy-500">Paying Now</span>
                            <span className="text-green-700 font-medium">+ Rs. {balancePaymentAmount.toLocaleString('en-IN')}</span>
                          </div>
                        )}
                        <div className="border-t border-navy-200 pt-2 flex justify-between">
                          <span className="text-navy-500">Pending Balance</span>
                          <span className={`font-semibold ${Math.max(0, confirmBooking.pending_amount - balancePaymentAmount) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            Rs. {Math.max(0, confirmBooking.pending_amount - balancePaymentAmount).toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>

                      {/* Collect Payment Now — only show if pending > 0 */}
                      {confirmBooking.pending_amount > 0 && (
                        <div className="mt-4 pt-4 border-t border-dashed border-navy-200">
                          <label className="block text-sm font-medium text-navy-700 mb-2">
                            <i className="ri-cash-line mr-1 text-green-600" />
                            Collect Payment Now
                          </label>
                          <div className="flex gap-3">
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400 text-sm">Rs.</span>
                              <input
                                type="number"
                                className="w-full pl-10 pr-4 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-400"
                                placeholder="Enter amount"
                                min={0}
                                max={confirmBooking.pending_amount}
                                value={balancePaymentAmount || ''}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  if (val > confirmBooking.pending_amount) return;
                                  setBalancePaymentAmount(val);
                                  if (val >= confirmBooking.pending_amount) {
                                    setGuestWillPayLater(false);
                                  }
                                }}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => setBalancePaymentAmount(confirmBooking.pending_amount)}
                              className="px-3 py-2.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              Pay Full
                            </button>
                          </div>
                          {balancePaymentAmount > 0 && (
                            <>
                              <div className="mt-3">
                                <label className="block text-xs font-medium text-navy-500 mb-1">Payment Method</label>
                                <select
                                  className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                                  value={arrivalPaymentMethod}
                                  onChange={(e) => setArrivalPaymentMethod(e.target.value as Payment['payment_method'])}
                                >
                                  <option value="cash">Cash</option>
                                  <option value="card">Card</option>
                                  <option value="upi">UPI</option>
                                  <option value="online">Online</option>
                                </select>
                              </div>
                              {balancePaymentAmount > 0 && balancePaymentAmount < confirmBooking.pending_amount && (
                                <p className="text-xs text-navy-500 mt-2">
                                  After this payment, Rs. {(confirmBooking.pending_amount - balancePaymentAmount).toLocaleString('en-IN')} still pending.
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Guest Will Pay Later Checkbox — only if balance still remains */}
                    {(() => {
                      const effPending = Math.max(0, confirmBooking.pending_amount - balancePaymentAmount);
                      const isPaid = effPending === 0;
                      return (
                        <>
                          {!isPaid && (
                            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                              <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="mt-0.5 w-4 h-4 rounded border-yellow-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer"
                                  checked={guestWillPayLater}
                                  onChange={(e) => setGuestWillPayLater(e.target.checked)}
                                />
                                <div>
                                  <span className="text-sm font-medium text-yellow-800">
                                    Guest will pay remaining Rs. {effPending.toLocaleString('en-IN')} later
                                  </span>
                                  <p className="text-xs text-yellow-600 mt-0.5">
                                    Guest has promised to clear the balance. Check-in will proceed with pending amount on record.
                                  </p>
                                </div>
                              </label>
                            </div>
                          )}

                          {/* ID Verification Note */}
                          <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                            <i className="ri-information-line text-yellow-600 mt-0.5" />
                            <div className="text-xs text-yellow-800">
                              <span className="font-medium">Verify ID at reception:</span> Please physically verify the guest&apos;s {g?.id_proof_type || 'ID'} before confirming check-in. ID Number: <span className="font-mono">{g?.id_proof_number}</span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-3">
                            <button
                              onClick={() => { setStep('search'); setConfirmBooking(null); setBalancePaymentAmount(0); setGuestWillPayLater(false); }}
                              className="flex-1 px-5 py-3 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-100 transition-colors cursor-pointer"
                            >
                              <i className="ri-arrow-left-line mr-2" />Back
                            </button>
                            {isPaid ? (
                              <button
                                onClick={confirmArrival}
                                className="flex-[2] px-6 py-3 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors cursor-pointer"
                              >
                                <i className="ri-login-box-line mr-2" />
                                Confirm Check-in — Guest Has Arrived
                              </button>
                            ) : guestWillPayLater ? (
                              <button
                                onClick={confirmArrival}
                                className="flex-[2] px-6 py-3 bg-yellow-500 text-white rounded-lg text-sm font-semibold hover:bg-yellow-600 transition-colors cursor-pointer"
                              >
                                <i className="ri-alert-line mr-2" />
                                Check-in with Rs. {effPending.toLocaleString('en-IN')} Pending
                              </button>
                            ) : (
                              <button
                                disabled
                                className="flex-[2] px-6 py-3 bg-gray-300 text-gray-500 rounded-lg text-sm font-semibold cursor-not-allowed"
                              >
                                <i className="ri-lock-line mr-2" />
                                Clear Balance or Select &quot;Pay Later&quot; to Check-in
                              </button>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ===== STEP: FORM (walk-in check-in form) ===== */}
      {step === 'form' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Guest Details */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-navy-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-navy-800">Walk-in Guest Details</h3>
              <span className="text-xs px-2.5 py-1 bg-navy-100 text-navy-600 rounded-full font-medium">
                New Check-in
              </span>
              {matchedGuest?.is_repeating && (
                <span className="text-xs px-2.5 py-1 bg-gold-50 text-gold-700 rounded-full font-medium">
                  <i className="ri-star-line mr-1" />Repeat Guest
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Full Name *</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  value={guestForm.full_name}
                  onChange={(e) => setGuestForm((p) => ({ ...p, full_name: e.target.value }))}
                  placeholder="Guest full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Phone *</label>
                <input
                  type="tel"
                  className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  value={guestForm.phone}
                  onChange={(e) => setGuestForm((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Email</label>
                <input
                  type="email"
                  className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  value={guestForm.email}
                  onChange={(e) => setGuestForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="guest@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">ID Proof Type</label>
                <select
                  className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  value={guestForm.id_proof_type}
                  onChange={(e) => setGuestForm((p) => ({ ...p, id_proof_type: e.target.value }))}
                >
                  {ID_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">ID Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  value={guestForm.id_proof_number}
                  onChange={(e) => setGuestForm((p) => ({ ...p, id_proof_number: e.target.value }))}
                  placeholder="ID number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Address</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  value={guestForm.address}
                  onChange={(e) => setGuestForm((p) => ({ ...p, address: e.target.value }))}
                  placeholder="Full address"
                />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-navy-100">
              <h3 className="font-semibold text-navy-800 mb-4">Room & Booking Details</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-navy-700 mb-2">Booking Mode</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setBookingMode('full_room'); setBookingForm((p) => ({ ...p, room_id: '' })); setSelectedBeds([]); }}
                    className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${bookingMode === 'full_room' ? 'border-navy-700 bg-navy-50 text-navy-800' : 'border-navy-100 text-navy-500 hover:border-navy-300'}`}
                  >
                    <i className="ri-home-4-line mr-1" /> Full Room
                  </button>
                  <button
                    onClick={() => { setBookingMode('bed'); setBookingForm((p) => ({ ...p, room_id: '' })); setSelectedBeds([]); }}
                    className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${bookingMode === 'bed' ? 'border-gold-500 bg-gold-50 text-gold-800' : 'border-navy-100 text-navy-500 hover:border-navy-300'}`}
                  >
                    <i className="ri-hotel-bed-line mr-1" /> Bed Booking
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Room *</label>
                  <select
                    className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                    value={bookingForm.room_id}
                    onChange={(e) => { setBookingForm((p) => ({ ...p, room_id: e.target.value })); setSelectedBeds([]); }}
                  >
                    <option value="">Select room</option>
                    {bookingMode === 'full_room'
                      ? availableRooms.map((r) => (
                        <option key={r.id} value={r.id}>
                          Room {r.room_number} - {r.category} (Rs. {r.tariff}/night)
                        </option>
                      ))
                      : sharedDormRooms.map((r) => {
                        const availBeds = getBedsForRoom(r.id).filter((b) => b.status === 'available').length;
                        return (
                          <option key={r.id} value={r.id}>
                            Room {r.room_number} - {r.category} ({availBeds} beds avail, Rs. {r.per_bed_price}/bed)
                          </option>
                        );
                      })
                    }
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Booking Type</label>
                  <select
                    className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                    value={bookingForm.booking_type}
                    onChange={(e) => setBookingForm((p) => ({ ...p, booking_type: e.target.value }))}
                  >
                    {BOOKING_TYPES.map((t) => (
                      <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
                    ))}
                  </select>
                </div>
              </div>
              {bookingMode === 'bed' && bookingForm.room_id && (
                <div className="mt-4 p-4 bg-gold-50 rounded-lg border border-gold-200">
                  <label className="block text-sm font-medium text-navy-700 mb-2">
                    Select Beds <span className="text-xs font-normal text-navy-400">({selectedBeds.length} selected)</span>
                  </label>
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                    {getBedsForRoom(bookingForm.room_id).sort((a, b) => a.bed_number - b.bed_number).map((bed) => {
                      const isSelected = selectedBeds.includes(bed.id);
                      const isAvailable = bed.status === 'available';
                      return (
                        <button
                          key={bed.id}
                          type="button"
                          onClick={() => {
                            if (!isAvailable && !isSelected) return;
                            setSelectedBeds((prev) => prev.includes(bed.id) ? prev.filter((id) => id !== bed.id) : [...prev, bed.id]);
                          }}
                          className={`p-2.5 rounded-lg border-2 text-center text-xs font-medium transition-all cursor-pointer
                            ${isSelected ? 'border-gold-500 bg-gold-100 text-gold-800' : ''}
                            ${isAvailable && !isSelected ? 'border-navy-200 bg-green-50 text-green-700 hover:border-navy-400' : ''}
                            ${!isAvailable && !isSelected ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : ''}
                          `}
                        >
                          <i className="ri-hotel-bed-line block mb-0.5" />
                          B{bed.bed_number}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Check-in Date *</label>
                  <input
                    type="datetime-local"
                    className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                    value={bookingForm.check_in}
                    onChange={(e) => setBookingForm((p) => ({ ...p, check_in: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Check-out Date *</label>
                  <input
                    type="datetime-local"
                    className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                    value={bookingForm.check_out}
                    onChange={(e) => setBookingForm((p) => ({ ...p, check_out: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-700 mb-1.5">Deposit (Rs.)</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                    value={bookingForm.deposit}
                    onChange={(e) => setBookingForm((p) => ({ ...p, deposit: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                {Number(bookingForm.deposit) > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-navy-700 mb-1.5">Payment Method</label>
                    <select
                      className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value as Payment['payment_method'])}
                    >
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="upi">UPI</option>
                      <option value="online">Online</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setStep('search')}
                className="px-5 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer"
              >
                Back to Search
              </button>
              <button
                onClick={handleWalkinCheckin}
                className="px-6 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer"
              >
                <i className="ri-login-box-line mr-2" />
                Complete Check-in
              </button>
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="bg-white rounded-xl border border-navy-100 p-6 h-fit">
            <h3 className="font-semibold text-navy-800 mb-4">Check-in Summary</h3>
            {bookingForm.room_id && (
              <div className="space-y-3 text-sm">
                {(() => {
                  const r = rooms.find((room) => room.id === bookingForm.room_id);
                  if (!r) return null;
                  const nights = bookingForm.check_in && bookingForm.check_out
                    ? Math.max(1, Math.ceil((new Date(bookingForm.check_out).getTime() - new Date(bookingForm.check_in).getTime()) / (1000 * 60 * 60 * 24)))
                    : 0;
                  const total = bookingMode === 'bed'
                    ? (r.per_bed_price || 0) * selectedBeds.length * nights
                    : r.tariff * nights;
                  return (
                    <>
                      <div className="flex justify-between py-2 border-b border-navy-100">
                        <span className="text-navy-500">Mode</span>
                        <span className="font-medium">{bookingMode === 'bed' ? 'Bed Booking' : 'Full Room'}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-navy-100">
                        <span className="text-navy-500">Room</span>
                        <span className="font-medium">{r.room_number} ({r.category})</span>
                      </div>
                      {bookingMode === 'bed' && selectedBeds.length > 0 && (
                        <div className="flex justify-between py-2 border-b border-navy-100">
                          <span className="text-navy-500">Beds</span>
                          <span className="font-medium">{selectedBeds.map((bid) => { const b = getBedsForRoom(r.id).find((x) => x.id === bid); return b ? `B${b.bed_number}` : ''; }).filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-2 border-b border-navy-100">
                        <span className="text-navy-500">Nights</span>
                        <span className="font-medium">{nights}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-navy-100">
                        <span className="text-navy-500">Tariff</span>
                        <span className="font-medium">
                          {bookingMode === 'bed'
                            ? `Rs. ${r.per_bed_price}/bed`
                            : `Rs. ${r.tariff}/night`
                          }
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-navy-100">
                        <span className="text-navy-500">Deposit</span>
                        <span className="font-medium">Rs. {Number(bookingForm.deposit).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between py-3 text-base font-bold text-navy-800">
                        <span>Total Amount</span>
                        <span>Rs. {total.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between py-2 text-sm text-red-600">
                        <span>Pending</span>
                        <span>Rs. {(total - Number(bookingForm.deposit)).toLocaleString('en-IN')}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            {!bookingForm.room_id && (
              <p className="text-sm text-navy-400">Select a room to see booking summary.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}